import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Card, Row, Col, DatePicker, Select, Table, Spin, Segmented, Statistic, message,
} from "antd";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip,
    Legend, ResponsiveContainer,
} from "recharts";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import {
    fetchBestSelling, fetchMostReturned, RankedItem, RankSortBy, RankOrder,
} from "../../api/reportApi";
import PageNavigation from "../Layout/PageNavigation";
import ExcelExportButton from "./ExcelExportButton";

const { RangePicker } = DatePicker;

type RankedItemsReportProps = {
    /** Which ranking to show: the best sellers or the most returned items. */
    variant: "best-selling" | "most-returned";
};

const num = (value: number | undefined) =>
    typeof value === "number" ? value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : "-";

/** Money made is green, money lost is red — a return's profit is always negative. */
const money = (value: number | undefined) => (
    <span style={{ color: (value || 0) < 0 ? "#dc2626" : (value || 0) > 0 ? "#16a34a" : undefined }}>
        {num(value)}
    </span>
);

/** Profit as a share of what was sold. Blank when nothing was sold. */
const margin = (profit: number | undefined, amount: number | undefined) =>
    amount ? `${((100 * (profit || 0)) / amount).toFixed(1)}%` : "-";

/**
 * A ranked item list, sorted on the server so the ordering holds across the
 * whole period and not just the page being looked at. Used for both the best
 * selling report and the most returned report — the two differ only in which
 * invoices they count.
 */
const RankedItemsReport: React.FC<RankedItemsReportProps> = ({ variant }) => {
    const { t } = useTranslation();
    const isReturns = variant === "most-returned";

    const [rows, setRows] = useState<RankedItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [sortBy, setSortBy] = useState<RankSortBy>("qty");
    const [order, setOrder] = useState<RankOrder>("desc");
    const [limit, setLimit] = useState(10);
    const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
        dayjs().startOf("month"),
        dayjs(),
    ]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                startDate: dateRange[0].format("YYYY-MM-DD"),
                endDate: dateRange[1].format("YYYY-MM-DD"),
                limit,
                sortBy,
                order,
            };
            const result = isReturns ? await fetchMostReturned(params) : await fetchBestSelling(params);
            setRows(result || []);
        } catch (error: any) {
            const apiMessage = error?.response?.data?.message;
            message.error(apiMessage || t("report_load_failed") || "Failed to load the report");
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [dateRange, limit, sortBy, order, isReturns, t]);

    useEffect(() => {
        load();
    }, [load]);

    const title = isReturns
        ? t("most_returned_report") || "Most returned items"
        : t("best_selling_report") || "Best selling items";

    const columns = [
        {
            title: "#",
            key: "rank",
            width: 60,
            render: (_: any, __: RankedItem, index: number) => index + 1,
        },
        {
            title: t("product"),
            key: "product",
            render: (_: any, row: RankedItem) => row.product?.name || "-",
            sorter: (a: RankedItem, b: RankedItem) =>
                (a.product?.name || "").localeCompare(b.product?.name || ""),
        },
        {
            title: t("code"),
            key: "code",
            render: (_: any, row: RankedItem) => row.product?.code || "-",
        },
        {
            title: t("qty"),
            dataIndex: "totalQty",
            key: "totalQty",
            sorter: (a: RankedItem, b: RankedItem) => a.totalQty - b.totalQty,
            render: num,
        },
        {
            title: t("totalAmount"),
            dataIndex: "totalAmount",
            key: "totalAmount",
            sorter: (a: RankedItem, b: RankedItem) => a.totalAmount - b.totalAmount,
            render: num,
        },
        {
            title: t("cost"),
            dataIndex: "totalCost",
            key: "totalCost",
            sorter: (a: RankedItem, b: RankedItem) => (a.totalCost || 0) - (b.totalCost || 0),
            render: num,
        },
        {
            title: t("profit"),
            dataIndex: "totalProfit",
            key: "totalProfit",
            sorter: (a: RankedItem, b: RankedItem) => (a.totalProfit || 0) - (b.totalProfit || 0),
            render: money,
        },
        {
            title: t("profit_margin"),
            key: "margin",
            render: (_: any, row: RankedItem) => margin(row.totalProfit, row.totalAmount),
            sorter: (a: RankedItem, b: RankedItem) =>
                (a.totalAmount ? (a.totalProfit || 0) / a.totalAmount : 0) -
                (b.totalAmount ? (b.totalProfit || 0) / b.totalAmount : 0),
        },
        {
            title: t("invoices") || "Invoices",
            dataIndex: "invoices",
            key: "invoices",
            sorter: (a: RankedItem, b: RankedItem) => a.invoices - b.invoices,
        },
    ];

    const chartData = rows.map(row => ({
        name: row.product?.name || row._id,
        [t("qty") as string]: row.totalQty,
        [t("totalAmount") as string]: row.totalAmount,
        [t("profit") as string]: row.totalProfit,
    }));

    const totals = useMemo(
        () => ({
            qty: rows.reduce((sum, row) => sum + (row.totalQty || 0), 0),
            amount: rows.reduce((sum, row) => sum + (row.totalAmount || 0), 0),
            cost: rows.reduce((sum, row) => sum + (row.totalCost || 0), 0),
            profit: rows.reduce((sum, row) => sum + (row.totalProfit || 0), 0),
            invoices: rows.reduce((sum, row) => sum + (row.invoices || 0), 0),
        }),
        [rows]
    );

    return (
        <>
            <PageNavigation title={title as string} />

            <Card style={{ margin: 16 }}>
                <Row gutter={[12, 12]} align="middle">
                    <Col xs={24} md={8}>
                        <RangePicker
                            style={{ width: "100%" }}
                            value={dateRange}
                            onChange={dates => {
                                if (dates && dates[0] && dates[1]) setDateRange([dates[0], dates[1]]);
                            }}
                        />
                    </Col>
                    <Col xs={12} md={5}>
                        <Segmented
                            block
                            value={sortBy}
                            onChange={value => setSortBy(value as RankSortBy)}
                            options={[
                                { label: t("sort_by_qty") || "By quantity", value: "qty" },
                                { label: t("sort_by_amount") || "By amount", value: "amount" },
                            ]}
                        />
                    </Col>
                    <Col xs={12} md={4}>
                        <Segmented
                            block
                            value={order}
                            onChange={value => setOrder(value as RankOrder)}
                            options={[
                                { label: t("highest_first") || "Highest", value: "desc" },
                                { label: t("lowest_first") || "Lowest", value: "asc" },
                            ]}
                        />
                    </Col>
                    <Col xs={12} md={3}>
                        <Select
                            style={{ width: "100%" }}
                            value={limit}
                            onChange={setLimit}
                            options={[10, 20, 50, 100].map(value => ({ value, label: `${t("top") || "Top"} ${value}` }))}
                        />
                    </Col>
                    <Col xs={12} md={4}>
                        <ExcelExportButton<RankedItem>
                            block
                            fileName={variant}
                            sheetName={title as string}
                            columns={[
                                { header: t("product") as string, key: (row: RankedItem) => row.product?.name || "-", width: 28 },
                                { header: t("code") as string, key: (row: RankedItem) => row.product?.code || "-", width: 16 },
                                { header: t("qty") as string, key: "totalQty", width: 12 },
                                { header: t("totalAmount") as string, key: "totalAmount", width: 16 },
                                { header: t("cost") as string, key: "totalCost", width: 16 },
                                { header: t("profit") as string, key: "totalProfit", width: 16 },
                                {
                                    header: t("profit_margin") as string,
                                    key: (row: RankedItem) => margin(row.totalProfit, row.totalAmount),
                                    width: 14,
                                },
                                { header: (t("invoices") as string) || "Invoices", key: "invoices", width: 12 },
                            ]}
                            rows={rows}
                            footer={[
                                [
                                    t("total") as string,
                                    "",
                                    totals.qty,
                                    totals.amount,
                                    totals.cost,
                                    totals.profit,
                                    margin(totals.profit, totals.amount),
                                    totals.invoices,
                                ],
                            ]}
                            title={[
                                title as string,
                                `${dateRange[0].format("YYYY-MM-DD")} → ${dateRange[1].format("YYYY-MM-DD")}`,
                            ]}
                        />
                    </Col>
                </Row>
            </Card>

            <Spin spinning={loading}>
                <Card style={{ margin: 16 }}>
                    <Table<RankedItem>
                        rowKey="_id"
                        columns={columns as any}
                        dataSource={rows}
                        pagination={false}
                        size="small"
                        scroll={{ x: true }}
                    />
                </Card>

                <Card style={{ margin: 16 }}>
                    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                        <Col xs={12} md={8} lg={4}>
                            <Statistic title={t("total_qty") || "Total quantity"} value={totals.qty} precision={2} />
                        </Col>
                        <Col xs={12} md={8} lg={4}>
                            <Statistic title={t("totalAmount")} value={totals.amount} precision={2} />
                        </Col>
                        <Col xs={12} md={8} lg={4}>
                            <Statistic title={t("total_cost")} value={totals.cost} precision={2} />
                        </Col>
                        <Col xs={12} md={8} lg={5}>
                            <Statistic
                                title={t("profit")}
                                value={totals.profit}
                                precision={2}
                                valueStyle={{ color: totals.profit < 0 ? "#dc2626" : "#16a34a" }}
                                suffix={totals.amount ? `(${margin(totals.profit, totals.amount)})` : undefined}
                            />
                        </Col>
                        <Col xs={12} md={8} lg={4}>
                            <Statistic title={t("invoices") || "Invoices"} value={totals.invoices} />
                        </Col>
                    </Row>

                    <div style={{ width: "100%", height: 340 }}>
                        <ResponsiveContainer>
                            <BarChart data={chartData} margin={{ bottom: 60 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" angle={-35} textAnchor="end" interval={0} height={80} />
                                {/* Profit keeps its own axis — quantities and money are nowhere near the same scale. */}
                                <YAxis yAxisId="left" />
                                <YAxis yAxisId="right" orientation="right" />
                                <ReTooltip />
                                <Legend />
                                <Bar
                                    yAxisId="left"
                                    dataKey={(sortBy === "qty" ? t("qty") : t("totalAmount")) as string}
                                    fill={isReturns ? "#dc2626" : "#16a34a"}
                                />
                                <Bar yAxisId="right" dataKey={t("profit") as string} fill="#2563eb" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </Card>
            </Spin>
        </>
    );
};

export default RankedItemsReport;
